// AUTO-GENERATED by vite-plugin-ken-modules. Do not edit.
// Re-runs on every Vite build; manual edits will be overwritten.
//
// Generated at: 2026-11-04T05:40:30.817Z
// Modules: 28

import type { ModuleRegistration } from "@ken/cockpit-types";
import { useAuthStore } from "@ken/auth";
import { ContributedTabsProvider } from "@ken/sdk";
import { createElement, type FC, type PropsWithChildren } from "react";

import * as accounts from "@ken/module-accounts";
import * as accounts_payable from "@ken/module-accounts_payable";
import * as classes from "@ken/module-classes";
import * as customers from "@ken/module-customers";
import * as dispatch from "@ken/module-dispatch";
import * as document_intake from "@ken/module-document_intake";
import * as employees from "@ken/module-employees";
import * as estimates from "@ken/module-estimates";
import * as expenses from "@ken/module-expenses";
import * as fixed_assets from "@ken/module-fixed_assets";
import * as fleet_management from "@ken/module-fleet_management";
import * as forms from "@ken/module-forms";
import * as inventory_management from "@ken/module-inventory_management";
import * as invoicing from "@ken/module-invoicing";
import * as jobs from "@ken/module-jobs";
import * as journal_entries from "@ken/module-journal_entries";
import * as messaging from "@ken/module-messaging";
import * as payments from "@ken/module-payments";
import * as products from "@ken/module-products";
import * as purchase_orders from "@ken/module-purchase_orders";
import * as quickbooks_wc_connector from "@ken/module-quickbooks_wc_connector";
import * as rates_and_units from "@ken/module-rates_and_units";
import * as sales_orders from "@ken/module-sales_orders";
import * as sales_receipts from "@ken/module-sales_receipts";
import * as secure_links from "@ken/module-secure_links";
import * as services from "@ken/module-services";
import * as suppliers from "@ken/module-suppliers";
import * as taxes from "@ken/module-taxes";

export type { ModuleRegistration } from "@ken/cockpit-types";

const NO_MODULES: readonly string[] = [];
const NO_PERMISSIONS: ReadonlySet<string> = new Set<string>();

function withContributedTabs(entry: ModuleRegistration): ModuleRegistration {
  const Inner = entry.Provider;
  const WithContributedTabs: FC<PropsWithChildren> = ({ children }) => {
    const session = useAuthStore((s) => s.session);
    return createElement(
      ContributedTabsProvider,
      {
        modules: MODULES,
        enabledModules: session?.enabledModules ?? NO_MODULES,
        permissions: session?.permissions ?? NO_PERMISSIONS,
      },
      Inner ? createElement(Inner, null, children) : children,
    );
  };
  return { ...entry, Provider: WithContributedTabs };
}

export const MODULES: readonly ModuleRegistration[] = [
  withContributedTabs({
    name: "accounts",
    manifest: {
      "identity": {
        "module": "accounts",
        "display_name": "Chart of Accounts",
        "description": "Universal Chart of Accounts module. Owns the chart_of_accounts canonical entity + the per-tenant account_types / cash_flow_classifications Principle-7 lookups + cross-source dedup.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Chart of accounts",
            "section": "accounting",
            "icon": "book-open",
            "order": 10,
            "path": "/accounts",
            "permissions": [
              "accounts.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "accounts.view"
        },
        {
          "id": "accounts.edit"
        }
      ]
    },
    createRoutes: accounts.createRoutes,
    Provider: accounts.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "accounts_payable",
    manifest: {
      "identity": {
        "module": "accounts_payable",
        "display_name": "Bills",
        "description": "Accounts payable — manage all bills the business owes (per ADR-025): supplier invoices, utilities, rent, subscriptions, taxes, insurance, other. The `bill_type` column on each bill discriminates. URL kept as /supplier-bills for backward compat; the module name + table are broadened. 0.3.0 added bill_payments foldin (mig 0170). 0.4.0 adds vendor_credits foldin (mig 0184) — supplier-issued credits at /vendor-credits.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Bills",
            "section": "procurement",
            "icon": "receipt",
            "order": 30,
            "path": "/supplier-bills",
            "permissions": [
              "accounts_payable.view"
            ]
          },
          {
            "label": "Bill payments",
            "section": "procurement",
            "icon": "banknote",
            "order": 40,
            "path": "/bill-payments",
            "permissions": [
              "accounts_payable.view"
            ]
          },
          {
            "label": "Vendor credits",
            "section": "procurement",
            "icon": "undo-2",
            "order": 50,
            "path": "/vendor-credits",
            "permissions": [
              "accounts_payable.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "accounts_payable.view"
        },
        {
          "id": "accounts_payable.edit"
        }
      ]
    },
    createRoutes: accounts_payable.createRoutes,
    Provider: accounts_payable.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "classes",
    manifest: {
      "identity": {
        "module": "classes",
        "display_name": "Classes",
        "description": "Module for QB Classes — the cost-center / department / location tagging dimension every transaction header optionally carries. Owns the classes canonical entity + cross-source dedup. List + story-page detail (ADR-0081/0082, config archetype).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Classes",
            "section": "accounting",
            "icon": "tag",
            "order": 50,
            "path": "/classes",
            "permissions": [
              "classes.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "classes.view"
        },
        {
          "id": "classes.edit"
        }
      ]
    },
    createRoutes: classes.createRoutes,
    Provider: classes.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "customers",
    manifest: {
      "identity": {
        "module": "customers",
        "display_name": "Customers",
        "description": "Universal customer management module. Owns the customer entity, list, profile, and edit flows. Used by every vertical.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Customers",
            "section": "sales",
            "icon": "users",
            "order": 10,
            "path": "/customers",
            "permissions": [
              "customers.view"
            ]
          }
        ]
      },
      "widgets": [
        {
          "id": "customers-overdue-summary",
          "component": "CustomersOverdueWidget",
          "default_size": "2x1",
          "title": "Outstanding receivables",
          "description": "Customers with overdue invoices, grouped by aging bucket.",
          "category": "finance",
          "allowed_roles": [
            "owner",
            "manager",
            "sales"
          ],
          "requires_modules": [
            "customers",
            "invoicing"
          ],
          "requires_permissions": [
            "customers.view"
          ]
        }
      ],
      "permissions": [
        {
          "id": "customers.view"
        },
        {
          "id": "customers.edit"
        },
        {
          "id": "customers.send_sms"
        }
      ]
    },
    createRoutes: customers.createRoutes,
    Provider: customers.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "dispatch",
    manifest: {
      "identity": {
        "module": "dispatch",
        "display_name": "Dispatch",
        "description": "A day's work for a trucking, delivery or service company (ADR-0261, D-282): a job written up for a customer with its stops in order, the truck, trailer and driver put on it (checked against Fleet so an out-of-service truck or a lapsed licence is never sent), the times the truck arrived and left each place, and the standby computed from those times, never typed. Owns dispatch_jobs, its stops, its assignments, its external ids, two company lists and the settings row. Never writes QuickBooks (D-036).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Dispatch",
            "section": "operations",
            "icon": "truck",
            "order": 19,
            "path": "/dispatch",
            "permissions": [
              "dispatch.view"
            ],
            "key": "dispatch",
            "badge_source": "/api/modules/dispatch/badge#count",
            "badge_label": "<n> jobs today with nobody on them"
          }
        ],
        "record_tabs": [
          {
            "entity_type": "FleetUnit",
            "key": "jobs",
            "label": "Jobs",
            "order": 60,
            "permissions": [
              "dispatch.view"
            ],
            "component": "DispatchRecordJobsTab"
          },
          {
            "entity_type": "Driver",
            "key": "jobs",
            "label": "Jobs",
            "order": 60,
            "permissions": [
              "dispatch.view"
            ],
            "component": "DispatchRecordJobsTab"
          },
          {
            "entity_type": "Customer",
            "key": "jobs",
            "label": "Jobs",
            "order": 90,
            "permissions": [
              "dispatch.view"
            ],
            "component": "DispatchRecordJobsTab"
          },
          {
            "entity_type": "Customer",
            "key": "dispatch",
            "label": "Dispatch",
            "order": 100,
            "permissions": [
              "dispatch.view"
            ],
            "component": "DispatchCustomerTermsTab"
          }
        ]
      },
      "permissions": [
        {
          "id": "dispatch.view"
        },
        {
          "id": "dispatch.edit"
        }
      ]
    },
    createRoutes: dispatch.createRoutes,
    Provider: dispatch.Provider ?? undefined,
    recordTabs: dispatch.recordTabs,
    linkPages: {},
  }),
  withContributedTabs({
    name: "document_intake",
    manifest: {
      "identity": {
        "module": "document_intake",
        "display_name": "Documents",
        "description": "Keeps every document a business is handed -- supplier bills, delivery notes, receipts -- the moment it arrives through any door, before anything reads it. Stores the file once per company behind a signed-in route, keeps who handed it over, when, and their words exactly as they arrived, gives it a DOC- reference a person can quote, recognises the same file arriving twice, and lets later messages join a stored document.\n",
        "kind": "source"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Documents",
            "section": "operations",
            "icon": "file-text",
            "order": 4,
            "path": "/documents",
            "permissions": [
              "document_intake.view_own"
            ],
            "badge_source": "/api/modules/document-intake/new-documents#new",
            "badge_label": "<n> new documents",
            "badge_topic": "document_intake.new_documents"
          },
          {
            "label": "Document types",
            "section": "operations",
            "icon": "notebook-pen",
            "order": 4,
            "path": "/settings/document_intake/types",
            "permissions": [
              "document_intake.admin"
            ],
            "key": "document-types"
          }
        ]
      },
      "permissions": [
        {
          "id": "document_intake.view"
        },
        {
          "id": "document_intake.view_own"
        },
        {
          "id": "document_intake.upload"
        },
        {
          "id": "document_intake.review"
        },
        {
          "id": "document_intake.admin"
        }
      ]
    },
    createRoutes: document_intake.createRoutes,
    Provider: document_intake.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "employees",
    manifest: {
      "identity": {
        "module": "employees",
        "display_name": "Employees",
        "description": "The people a company employs, as business records — name, contact details, role, employment dates and the identifiers that link them to other records. Owns the employees canonical entity, its contact-channel and dated-note children, cross-source dedup and the employee_types lookup. Never payroll: no wage, rate, hours, withholding or pay run, by owner ruling D-044 item 4.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Employees",
            "section": "operations",
            "icon": "user-round",
            "order": 10,
            "path": "/employees",
            "permissions": [
              "employees.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "employees.view"
        },
        {
          "id": "employees.edit"
        }
      ]
    },
    createRoutes: employees.createRoutes,
    Provider: employees.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "estimates",
    manifest: {
      "identity": {
        "module": "estimates",
        "display_name": "Estimates",
        "description": "Estimates — pre-sale quotes / proposals with line items. Trades & Services primary surface; optional for Wholesale.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Estimates",
            "section": "sales",
            "icon": "file-spreadsheet",
            "order": 30,
            "path": "/estimates",
            "permissions": [
              "estimates.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "estimates.view"
        },
        {
          "id": "estimates.edit"
        }
      ]
    },
    createRoutes: estimates.createRoutes,
    Provider: estimates.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "expenses",
    manifest: {
      "identity": {
        "module": "expenses",
        "display_name": "Expenses",
        "description": "Direct cash-out expenses — Check + CreditCardCharge ingested from QuickBooks. List/detail with GL-account + catalog line items, billable pass-through to customer/job. Universal (both Trades & Services AND Wholesale templates).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Expenses",
            "section": "procurement",
            "icon": "credit-card",
            "order": 60,
            "path": "/expenses",
            "permissions": [
              "expenses.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "expenses.view"
        },
        {
          "id": "expenses.edit"
        }
      ]
    },
    createRoutes: expenses.createRoutes,
    Provider: expenses.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "fixed_assets",
    manifest: {
      "identity": {
        "module": "fixed_assets",
        "display_name": "Fixed Assets",
        "description": "Universal Fixed Assets (capital asset register) module. Owns the fixed_assets canonical entity + cross-source dedup. Tracks owned equipment, vehicles, leasehold improvements, computer hardware. Required FK to chart_of_accounts; optional supplier FK.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Fixed assets",
            "section": "accounting",
            "icon": "building-2",
            "order": 40,
            "path": "/fixed-assets",
            "permissions": [
              "fixed_assets.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "fixed_assets.view"
        },
        {
          "id": "fixed_assets.edit"
        }
      ]
    },
    createRoutes: fixed_assets.createRoutes,
    Provider: fixed_assets.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "fleet_management",
    manifest: {
      "identity": {
        "module": "fleet_management",
        "display_name": "Fleet",
        "description": "The company's own and its contractors' trucks, trailers and other units (ADR-0259, D-275): the unit number everyone uses, what kind of unit it is and who owns it, roadside identification, capacity, whether it is in service, and the registration and inspection dates that come due. Owns fleet_units, its external ids, Fleet's four company lists and the due-window setting. Never writes QuickBooks (D-036).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Fleet",
            "section": "operations",
            "icon": "truck",
            "order": 20,
            "path": "/fleet",
            "permissions": [
              "fleet_management.view"
            ],
            "key": "fleet",
            "badge_source": "/api/modules/fleet-management/due-count#count",
            "badge_label": "<n> due or overdue"
          },
          {
            "label": "Drivers",
            "section": "operations",
            "icon": "users",
            "order": 21,
            "path": "/fleet/drivers",
            "permissions": [
              "fleet_management.view"
            ],
            "key": "fleet-drivers"
          }
        ],
        "record_tabs": [
          {
            "entity_type": "Employee",
            "key": "driver",
            "label": "Driver",
            "order": 45,
            "permissions": [
              "fleet_management.view"
            ],
            "component": "EmployeeDriverTab"
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "fleet_management.view"
        },
        {
          "id": "fleet_management.edit"
        },
        {
          "id": "fleet_management.view_licence_numbers"
        }
      ]
    },
    createRoutes: fleet_management.createRoutes,
    Provider: fleet_management.Provider ?? undefined,
    recordTabs: fleet_management.recordTabs,
    linkPages: {},
  }),
  withContributedTabs({
    name: "forms",
    manifest: {
      "identity": {
        "module": "forms",
        "display_name": "Forms",
        "description": "The company's own paper forms (ADR-0262, D-293, D-298): each form is a versioned copy of the paper on a twelve-column grid, its printed words kept word for word; a published version never changes. A copy is filled on a driver's phone through a link or typed in the office; once approved, Forms hands it to the module that declared its type (the field ticket to Dispatch) through that module's own doorway. Never writes QuickBooks.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Forms",
            "section": "operations",
            "icon": "file-text",
            "order": 21,
            "path": "/forms",
            "permissions": [
              "forms.view"
            ],
            "key": "forms",
            "badge_source": "/api/modules/forms/badge#count",
            "badge_label": "<n> tickets waiting for approval"
          }
        ],
        "link_pages": [
          {
            "kind": "form_fill",
            "component": "FormFillLinkPage"
          }
        ]
      },
      "permissions": [
        {
          "id": "forms.view"
        },
        {
          "id": "forms.fill"
        },
        {
          "id": "forms.edit"
        }
      ]
    },
    createRoutes: forms.createRoutes,
    Provider: forms.Provider ?? undefined,
    recordTabs: {},
    linkPages: forms.linkPages,
  }),
  withContributedTabs({
    name: "inventory_management",
    manifest: {
      "identity": {
        "module": "inventory_management",
        "display_name": "Inventory",
        "description": "Inventory management — stock locations, stock balances, ledger of movements, batches with best-before + FEFO, receiving orders, and supplier pricing. The full stock-side flow per ADR-024 (inventory_batches was folded in 2026-05-27).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Inventory",
            "section": "inventory",
            "icon": "warehouse",
            "order": 10,
            "path": "/inventory",
            "permissions": [
              "inventory_management.view"
            ]
          },
          {
            "label": "Batches",
            "section": "inventory",
            "icon": "scroll-text",
            "order": 20,
            "path": "/inventory/batches",
            "permissions": [
              "inventory_management.batches.view"
            ]
          },
          {
            "label": "Arrivals",
            "section": "inventory",
            "icon": "package-check",
            "order": 30,
            "path": "/inventory/arrivals",
            "permissions": [
              "inventory_management.view"
            ]
          },
          {
            "label": "Short deliveries",
            "section": "inventory",
            "icon": "receipt",
            "order": 35,
            "path": "/inventory/short-deliveries",
            "permissions": [
              "inventory_management.view"
            ]
          },
          {
            "label": "Adjustments",
            "section": "inventory",
            "icon": "clipboard-list",
            "order": 40,
            "path": "/inventory/adjustments",
            "permissions": [
              "inventory_management.view"
            ]
          },
          {
            "label": "Transfers",
            "section": "inventory",
            "icon": "arrow-left-right",
            "order": 50,
            "path": "/inventory/transfers",
            "permissions": [
              "inventory_management.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "inventory_management.view"
        },
        {
          "id": "inventory_management.edit"
        },
        {
          "id": "inventory_management.batches.view"
        },
        {
          "id": "inventory_management.batches.edit"
        }
      ]
    },
    createRoutes: inventory_management.createRoutes,
    Provider: inventory_management.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "invoicing",
    manifest: {
      "identity": {
        "module": "invoicing",
        "display_name": "Invoices",
        "description": "Invoicing — invoice list/detail with line items + customer payments + credit memos issued to customers. Universal (every vertical). 0.2.0 adds /credit-memos sub-list (Sprint 2 mig 0184/0185 fold-in).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Invoices",
            "section": "sales",
            "icon": "file-text",
            "order": 50,
            "path": "/invoices",
            "permissions": [
              "invoicing.view"
            ]
          },
          {
            "label": "Credits",
            "section": "sales",
            "icon": "rotate-ccw",
            "order": 60,
            "path": "/credit-memos",
            "permissions": [
              "invoicing.view"
            ]
          }
        ],
        "record_tabs": [
          {
            "entity_type": "Customer",
            "key": "invoices",
            "label": "Invoices",
            "order": 30,
            "permissions": [
              "invoicing.view"
            ],
            "component": "CustomerInvoicesTab"
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "invoicing.view"
        },
        {
          "id": "invoicing.edit"
        }
      ]
    },
    createRoutes: invoicing.createRoutes,
    Provider: invoicing.Provider ?? undefined,
    recordTabs: invoicing.recordTabs,
    linkPages: {},
  }),
  withContributedTabs({
    name: "jobs",
    manifest: {
      "identity": {
        "module": "jobs",
        "display_name": "Jobs",
        "description": "Jobs (a.k.a. customer sub-accounts) — projects, engagements, or work-orders a customer is being billed for separately. First Ken module scoped to the service vertical: construction projects, agency campaigns, HVAC service calls, hockey-team player accounts, etc. ADR-057.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Jobs",
            "section": "sales",
            "icon": "hammer",
            "order": 20,
            "path": "/jobs",
            "permissions": [
              "jobs.view"
            ]
          }
        ]
      },
      "permissions": [
        {
          "id": "jobs.view"
        },
        {
          "id": "jobs.edit"
        }
      ]
    },
    createRoutes: jobs.createRoutes,
    Provider: jobs.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "journal_entries",
    manifest: {
      "identity": {
        "module": "journal_entries",
        "display_name": "Journal Entries",
        "description": "Journal Entries — accountant-posted manual debit/credit adjustments (depreciation, accruals, opening balances, year-end true-ups). Off by default; enable in Settings when an accountant is invited.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Journal entries",
            "section": "accounting",
            "icon": "notebook-pen",
            "order": 30,
            "path": "/journal-entries",
            "permissions": [
              "journal_entries.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "journal_entries.view"
        },
        {
          "id": "journal_entries.edit"
        }
      ]
    },
    createRoutes: journal_entries.createRoutes,
    Provider: journal_entries.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "messaging",
    manifest: {
      "identity": {
        "module": "messaging",
        "display_name": "Messaging",
        "description": "Texting for every business on Ken. Captures every text the telephony gateway delivers (and, at a number's switch, the history read from Customer Connect) as raw, keeps each message's words once so retention and erasure can really remove them, and attaches every conversation to the customer, supplier or staff member it belongs to. Sends go out only through Layer 5's governed actions. Owns messages, conversations, assignments, the address book with its own opt-outs, and the consent table.\n",
        "kind": "source"
      },
      "frontend": {
        "navigation": [
          {
            "label": "SMS Conversations",
            "section": "operations",
            "icon": "message-square",
            "order": 5,
            "path": "/messages",
            "permissions": [
              "messaging.view"
            ],
            "key": "messages",
            "badge_source": "/api/modules/messaging/inbox/counts#needs_a_person",
            "badge_label": "<n> need a person",
            "badge_topic": "messaging.inbox"
          },
          {
            "label": "Messaging",
            "section": "integrations",
            "icon": "plug",
            "order": 20,
            "path": "/settings/integrations/messaging",
            "permissions": [
              "messaging.view"
            ],
            "key": "messaging-settings"
          }
        ],
        "record_tabs": [
          {
            "entity_type": "Customer",
            "key": "messages",
            "label": "Messages",
            "order": 40,
            "permissions": [
              "messaging.view"
            ],
            "component": "RecordMessagesTab"
          },
          {
            "entity_type": "Supplier",
            "key": "messages",
            "label": "Messages",
            "order": 40,
            "permissions": [
              "messaging.view"
            ],
            "component": "RecordMessagesTab"
          },
          {
            "entity_type": "Employee",
            "key": "messages",
            "label": "Messages",
            "order": 40,
            "permissions": [
              "messaging.view"
            ],
            "component": "RecordMessagesTab"
          }
        ]
      },
      "permissions": [
        {
          "id": "messaging.view"
        },
        {
          "id": "messaging.send"
        },
        {
          "id": "messaging.admin"
        }
      ]
    },
    createRoutes: messaging.createRoutes,
    Provider: messaging.Provider ?? undefined,
    recordTabs: messaging.recordTabs,
    linkPages: {},
  }),
  withContributedTabs({
    name: "payments",
    manifest: {
      "identity": {
        "module": "payments",
        "display_name": "Payments",
        "description": "Universal payments module. Owns customer-payment + deposit views. Deposits foldin lives here per master plan.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Payments received",
            "section": "sales",
            "icon": "credit-card",
            "order": 80,
            "path": "/payments",
            "permissions": [
              "payments.view"
            ]
          },
          {
            "label": "Deposits",
            "section": "sales",
            "icon": "landmark",
            "order": 90,
            "path": "/payments/deposits",
            "permissions": [
              "payments.view"
            ],
            "key": "deposits"
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "payments.view"
        },
        {
          "id": "payments.edit"
        },
        {
          "id": "payments.terms.view"
        },
        {
          "id": "payments.terms.edit"
        }
      ]
    },
    createRoutes: payments.createRoutes,
    Provider: payments.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "products",
    manifest: {
      "identity": {
        "module": "products",
        "display_name": "Products",
        "description": "Universal product catalog module. Owns the product entity, hierarchical categories, brands, external-platform identity and its images. Unit-of-measure sets and price levels belong to Rates and units (ADR-0276), which Products requires. Used by every vertical.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Products",
            "section": "catalog",
            "icon": "package",
            "order": 10,
            "path": "/products",
            "permissions": [
              "products.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "products.view"
        },
        {
          "id": "products.edit"
        }
      ]
    },
    createRoutes: products.createRoutes,
    Provider: products.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "purchase_orders",
    manifest: {
      "identity": {
        "module": "purchase_orders",
        "display_name": "Purchase Orders",
        "description": "Purchase order management — universal feature module (per ADR-053). Every vertical that buys from suppliers uses POs: distribution restocking, clinic supplies, mechanic parts, contractor materials, professional services, equipment. The `purchase_type` enum on each PO records the kind (inventory_purchase / services / equipment / other).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Purchase orders",
            "section": "procurement",
            "icon": "clipboard-list",
            "order": 20,
            "path": "/purchase-orders",
            "permissions": [
              "purchase_orders.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "purchase_orders.view"
        },
        {
          "id": "purchase_orders.edit"
        }
      ]
    },
    createRoutes: purchase_orders.createRoutes,
    Provider: purchase_orders.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "quickbooks_wc_connector",
    manifest: {
      "identity": {
        "module": "quickbooks_wc_connector",
        "display_name": "QuickBooks (Web Connector)",
        "description": "QuickBooks Desktop source via native QBWC SOAP. Replaces Conductor (ADR-016). Entity-agnostic SOAP pipe + per-tenant capability discovery + owner-driven sync config + per-entity adapters. Adding a QB entity = adding an adapter, never changing the connector.",
        "kind": "source"
      },
      "frontend": {
        "navigation": [
          {
            "label": "QuickBooks",
            "section": "integrations",
            "icon": "plug",
            "order": 10,
            "path": "/settings/integrations/quickbooks",
            "permissions": [
              "quickbooks_wc_connector.view"
            ],
            "key": "quickbooks-wc"
          }
        ]
      },
      "permissions": [
        {
          "id": "quickbooks_wc_connector.admin"
        },
        {
          "id": "quickbooks_wc_connector.view"
        }
      ]
    },
    createRoutes: quickbooks_wc_connector.createRoutes,
    Provider: quickbooks_wc_connector.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "rates_and_units",
    manifest: {
      "identity": {
        "module": "rates_and_units",
        "display_name": "Rates and units",
        "description": "Rates and units: the unit-of-measure sets things are counted and sold in, and the price levels that say what a given customer pays. Shared by products, services, customers, jobs, documents and bills, so it is owned here rather than by any one of them (ADR-0276). Works with Products switched off.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Units of measure",
            "section": "catalog",
            "icon": "ruler",
            "order": 30,
            "path": "/rates-and-units/units-of-measure",
            "permissions": [
              "rates_and_units.view"
            ],
            "key": "units-of-measure"
          },
          {
            "label": "Price levels",
            "section": "catalog",
            "icon": "tag",
            "order": 40,
            "path": "/rates-and-units/price-levels",
            "permissions": [
              "rates_and_units.view"
            ],
            "key": "price-levels"
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "rates_and_units.view"
        },
        {
          "id": "rates_and_units.edit"
        }
      ]
    },
    createRoutes: rates_and_units.createRoutes,
    Provider: rates_and_units.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "sales_orders",
    manifest: {
      "identity": {
        "module": "sales_orders",
        "display_name": "Sales Orders",
        "description": "Sales Orders — pre-invoice customer commitments with line items. Wholesale primary surface; optional for Trades & Services.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Sales orders",
            "section": "sales",
            "icon": "shopping-cart",
            "order": 40,
            "path": "/sales-orders",
            "permissions": [
              "sales_orders.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "sales_orders.view"
        },
        {
          "id": "sales_orders.edit"
        }
      ]
    },
    createRoutes: sales_orders.createRoutes,
    Provider: sales_orders.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "sales_receipts",
    manifest: {
      "identity": {
        "module": "sales_receipts",
        "display_name": "Sales Receipts",
        "description": "Sales receipts — point-of-sale cash sales ingested from QuickBooks. List/detail with line items and customer-or-job parent. Universal (every vertical).",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Sales receipts",
            "section": "sales",
            "icon": "receipt",
            "order": 70,
            "path": "/sales-receipts",
            "permissions": [
              "sales_receipts.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "sales_receipts.view"
        },
        {
          "id": "sales_receipts.edit"
        }
      ]
    },
    createRoutes: sales_receipts.createRoutes,
    Provider: sales_receipts.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "secure_links",
    manifest: {
      "identity": {
        "module": "secure_links",
        "display_name": "Secure links",
        "description": "Makes every no-login link and checks every visitor (ADR-0269, D-318): a person opens /l/{secret} on a phone, proves it with a code texted to their number, and sees the asking module's page. Holds no feature content: the asking module owns the page, the data and the kind's rules (contributes.link_kinds, ADR-0271). Only the SHA-256 of a secret and an HMAC of a code are ever stored.",
        "kind": "feature"
      },
      "permissions": [
        {
          "id": "secure_links.view"
        }
      ]
    },
    createRoutes: secure_links.createRoutes,
    Provider: secure_links.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "services",
    manifest: {
      "identity": {
        "module": "services",
        "display_name": "Services",
        "description": "Universal services catalog module. Owns the service entity (non-stocked sellable offerings — labor, consulting, charges) split off from the polymorphic QB Items List per ADR-0073.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Services",
            "section": "catalog",
            "icon": "briefcase",
            "order": 20,
            "path": "/services",
            "permissions": [
              "services.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "services.view"
        },
        {
          "id": "services.edit"
        }
      ]
    },
    createRoutes: services.createRoutes,
    Provider: services.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "suppliers",
    manifest: {
      "identity": {
        "module": "suppliers",
        "display_name": "Suppliers",
        "description": "Universal supplier directory module. Owns the supplier entity (vendors a.k.a. suppliers) and supplier-platform external-id dedup.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Suppliers",
            "section": "procurement",
            "icon": "factory",
            "order": 10,
            "path": "/suppliers",
            "permissions": [
              "suppliers.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "suppliers.view"
        },
        {
          "id": "suppliers.edit"
        }
      ]
    },
    createRoutes: suppliers.createRoutes,
    Provider: suppliers.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
  withContributedTabs({
    name: "taxes",
    manifest: {
      "identity": {
        "module": "taxes",
        "display_name": "Taxes",
        "description": "Universal taxes module. Houses sales-tax codes (ItemSalesTaxRet + ItemSalesTaxGroupRet) and discount rules (ItemDiscountRet) on a single navigation entry with two sub-views. Used by every vertical that bills sales tax or applies discounts.",
        "kind": "feature"
      },
      "frontend": {
        "navigation": [
          {
            "label": "Taxes",
            "section": "accounting",
            "icon": "percent",
            "order": 20,
            "path": "/taxes",
            "permissions": [
              "taxes.view"
            ]
          }
        ]
      },
      "widgets": [],
      "permissions": [
        {
          "id": "taxes.view"
        },
        {
          "id": "taxes.edit"
        }
      ]
    },
    createRoutes: taxes.createRoutes,
    Provider: taxes.Provider ?? undefined,
    recordTabs: {},
    linkPages: {},
  }),
] as const;

export const MODULE_BY_NAME: Readonly<Record<string, ModuleRegistration>> = Object.freeze(
  Object.fromEntries(MODULES.map((m) => [m.name, m])),
);
